import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Select, Typography, Statistic, Table, Tag, Space, Button,
  Progress, Alert, Empty, Tooltip, Avatar, Badge,
} from 'antd';
import {
  BarChartOutlined, ClockCircleOutlined, FireOutlined, RiseOutlined,
  UserOutlined, AlertOutlined, ReloadOutlined, RightOutlined,
  EnvironmentOutlined, TeamOutlined, ExportOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  LineChart, Line, Legend, PieChart, Pie, Cell, AreaChart, Area,
} from 'recharts';
import {
  getEvents, getAnalyticsSummary, getPeakHours, getDailyTrend, getTopMovers,
  getNoShows, getFirstArrivals, getDwellTime, getZoneFlow, getCategoryStats,
  getCapacityStatus,
} from '../api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';
const ACCENT  = '#1B2B8C';
const TEAL    = '#00838F';
const COLORS  = ['#0F1D6B', '#1B2B8C', '#00838F', '#00BCD4', '#f59e0b', '#10b981', '#ec4899', '#8b5cf6'];

const fmtMins = (m) => {
  if (!m || m < 1) return '< 1 min';
  if (m < 60) return `${Math.round(m)}m`;
  const h = Math.floor(m / 60);
  const mm = Math.round(m % 60);
  return `${h}h ${mm}m`;
};

const fmtTime = (t) => t ? dayjs(t).format('DD MMM HH:mm:ss') : '-';

export default function Analytics() {
  const navigate = useNavigate();
  const [events, setEvents]   = useState([]);
  const [eventId, setEventId] = useState(null);
  const [loading, setLoading] = useState(false);

  const [summary,    setSummary]    = useState({});
  const [peakHours,  setPeakHours]  = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [topMovers,  setTopMovers]  = useState([]);
  const [noShows,    setNoShows]    = useState([]);
  const [firstArrivals, setFirstArrivals] = useState([]);
  const [dwellTime,  setDwellTime]  = useState([]);
  const [zoneFlow,   setZoneFlow]   = useState([]);
  const [catStats,   setCatStats]   = useState([]);
  const [capacity,   setCapacity]   = useState([]);

  // Load events on mount
  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const active = ev.find(e => e.status === 'active') || ev[0];
      if (active) setEventId(active.id);
    }).catch(() => {});
  }, []);

  const loadAll = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    const p = { event_id: eventId };
    Promise.all([
      getAnalyticsSummary(p),
      getPeakHours(p),
      getDailyTrend({ ...p, days: 7 }),
      getTopMovers({ ...p, limit: 10 }),
      getNoShows(p),
      getFirstArrivals({ ...p, limit: 10 }),
      getDwellTime(p),
      getZoneFlow(p),
      getCategoryStats(p),
      getCapacityStatus(p),
    ]).then(([s, ph, dt, tm, ns, fa, dw, zf, cs, cap]) => {
      setSummary(s.data.data || {});
      setPeakHours(ph.data.data || []);
      setDailyTrend(dt.data.data || []);
      setTopMovers(tm.data.data || []);
      setNoShows(ns.data.data || []);
      setFirstArrivals(fa.data.data || []);
      setDwellTime(dw.data.data || []);
      setZoneFlow(zf.data.data || []);
      setCatStats(cs.data.data || []);
      setCapacity(cap.data.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const criticalZones = capacity.filter(z => z.alert === 'critical');
  const warnZones     = capacity.filter(z => z.alert === 'warning');

  return (
    <div>
      {/* ── Header ───────────────────────────────────────────────────────────── */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <BarChartOutlined style={{ marginRight: 8 }} />Analytics & Insights
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Deep insights into delegate behaviour, zone activity & event KPIs
          </Text>
        </Col>
        <Col>
          <Space>
            <Select
              value={eventId}
              onChange={setEventId}
              style={{ width: 240 }}
              placeholder="Select Event"
              loading={!events.length}
            >
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={loadAll} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {/* ── Capacity alerts banner ───────────────────────────────────────────── */}
      {(criticalZones.length > 0 || warnZones.length > 0) && (
        <Alert
          type={criticalZones.length ? 'error' : 'warning'}
          showIcon
          icon={<AlertOutlined />}
          style={{ marginBottom: 16, borderRadius: 8 }}
          message={
            criticalZones.length
              ? `${criticalZones.length} zone(s) at or over capacity`
              : `${warnZones.length} zone(s) approaching capacity (≥ 80%)`
          }
          description={
            <Space wrap>
              {[...criticalZones, ...warnZones].slice(0, 8).map(z => (
                <Tag key={z.location_id} color={z.alert === 'critical' ? 'red' : 'orange'}>
                  {z.location_name}: {z.current_count}/{z.capacity} ({z.percent_full}%)
                </Tag>
              ))}
            </Space>
          }
        />
      )}

      {/* ── KPI Cards ────────────────────────────────────────────────────────── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col xs={12} md={6} lg={4}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${PRIMARY}` }}>
            <Statistic title="Registered" value={summary.registered || 0} prefix={<TeamOutlined />} valueStyle={{ color: PRIMARY }} />
          </Card>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid #10b981` }}>
            <Statistic title="Currently Present" value={summary.present || 0} prefix={<UserOutlined />} valueStyle={{ color: '#10b981' }} />
          </Card>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${TEAL}` }}>
            <Statistic title="Total Active" value={summary.active || 0} prefix={<RiseOutlined />} valueStyle={{ color: TEAL }} />
          </Card>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid #f59e0b` }}>
            <Statistic title="Peak Hour" value={summary.peak_hour != null ? `${String(summary.peak_hour).padStart(2,'0')}:00` : '-'}
              prefix={<FireOutlined />} valueStyle={{ color: '#f59e0b' }} />
          </Card>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${ACCENT}` }}>
            <Statistic title="Avg Dwell" value={fmtMins(summary.avg_dwell_minutes)}
              prefix={<ClockCircleOutlined />} valueStyle={{ color: ACCENT, fontSize: 22 }} />
          </Card>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid #ef4444` }}>
            <Statistic title="No-Shows" value={summary.no_shows || 0}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>{summary.no_show_pct}%</Text>}
              valueStyle={{ color: '#ef4444' }} />
          </Card>
        </Col>
      </Row>

      {/* ── Row: Peak Hours + Daily Trend ────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          <Card title={<Space><FireOutlined style={{ color: '#f59e0b' }} /> Peak Hours (today)</Space>} style={{ borderRadius: 10 }}>
            {peakHours.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={peakHours} margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                  <defs>
                    <linearGradient id="peakBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"  stopColor={TEAL} stopOpacity={0.95}/>
                      <stop offset="100%" stopColor={TEAL} stopOpacity={0.45}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RTooltip />
                  <Bar dataKey="scans" fill="url(#peakBar)" radius={[6,6,0,0]} name="Scans" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title={<Space><RiseOutlined style={{ color: TEAL }} /> 7-Day Attendance Trend</Space>} style={{ borderRadius: 10 }}>
            {dailyTrend.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={dailyTrend} margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                  <defs>
                    <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"  stopColor={PRIMARY} stopOpacity={0.6}/>
                      <stop offset="100%" stopColor={PRIMARY} stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => dayjs(v).format('DD MMM')} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RTooltip labelFormatter={v => dayjs(v).format('DD MMM YYYY')} />
                  <Area type="monotone" dataKey="unique_delegates" stroke={PRIMARY} fill="url(#trendArea)" name="Unique Delegates" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>

      {/* ── Row: Dwell Time + Category Stats ─────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          <Card title={<Space><ClockCircleOutlined style={{ color: ACCENT }} /> Average Dwell Time per Zone</Space>} style={{ borderRadius: 10 }}>
            {dwellTime.length === 0 ? <Empty description="No movement data yet" /> : (
              <Table
                dataSource={dwellTime}
                rowKey="location_id"
                pagination={false}
                size="small"
                columns={[
                  { title: 'Zone', dataIndex: 'location_name', render: v => (
                    <Space><EnvironmentOutlined style={{ color: TEAL }} /><Text strong>{v}</Text></Space>
                  ) },
                  { title: 'Visitors', dataIndex: 'unique_visitors', align: 'center', width: 90,
                    render: v => <Tag color="blue">{v}</Tag> },
                  { title: 'Visits', dataIndex: 'total_visits', align: 'center', width: 80 },
                  { title: 'Avg Dwell', dataIndex: 'avg_dwell_minutes', align: 'right', width: 110,
                    render: v => <Text strong style={{ color: ACCENT }}>{fmtMins(v)}</Text> },
                  { title: 'Max', dataIndex: 'max_dwell_minutes', align: 'right', width: 90,
                    render: v => <Text type="secondary">{fmtMins(v)}</Text> },
                ]}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title={<Space><TeamOutlined style={{ color: PRIMARY }} /> Category Breakdown</Space>} style={{ borderRadius: 10 }}>
            {catStats.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={catStats}
                    dataKey="total_registered"
                    nameKey="category_name"
                    cx="50%" cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                  >
                    {catStats.map((c, i) => (
                      <Cell key={i} fill={c.category_color || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip formatter={(v, n, p) => [`${v} delegates`, p.payload.category_name]} />
                </PieChart>
              </ResponsiveContainer>
            )}
            <div style={{ marginTop: 10 }}>
              {catStats.map(c => (
                <div key={c.category_id} style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '6px 0', borderTop: '1px solid #f0f0f0', fontSize: 12,
                }}>
                  <Space>
                    <span style={{
                      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                      background: c.category_color || '#888'
                    }}/>
                    <Text>{c.category_name}</Text>
                  </Space>
                  <Space size={12}>
                    <Tag>{c.total_registered} reg</Tag>
                    <Tag color="green">{c.present_now} present</Tag>
                  </Space>
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Row: Top Movers + First Arrivals ─────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card title={<Space><RiseOutlined style={{ color: '#f59e0b' }} /> Top 10 Most Active Delegates</Space>} style={{ borderRadius: 10 }}
            extra={<Button size="small" type="link" onClick={() => navigate('/delegates')}>View all</Button>}>
            {topMovers.length === 0 ? <Empty /> : (
              <Table
                dataSource={topMovers}
                rowKey="delegate_id"
                pagination={false}
                size="small"
                onRow={(r) => ({ onClick: () => navigate(`/delegate-journey/${r.delegate_id}?event_id=${eventId}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: '#', width: 45, render: (_, __, i) => (
                    <Avatar size={26} style={{
                      background: i === 0 ? '#fbbf24' : i === 1 ? '#9ca3af' : i === 2 ? '#cd7f32' : '#e5e7eb',
                      color: i < 3 ? '#fff' : '#374151', fontSize: 12, fontWeight: 700,
                    }}>{i+1}</Avatar>
                  )},
                  { title: 'Name', dataIndex: 'delegate_name', render: (v, r) => (
                    <Space direction="vertical" size={0}>
                      <Text strong>{v}</Text>
                      <Text type="secondary" style={{ fontSize: 10 }}>{r.company || '-'}</Text>
                    </Space>
                  )},
                  { title: 'Cat', dataIndex: 'category', width: 80, render: (v, r) =>
                    v ? <Tag color={r.category_color}>{v}</Tag> : '-' },
                  { title: 'Moves', dataIndex: 'movement_count', align: 'center', width: 70,
                    render: v => <Tag color="blue" style={{ fontWeight: 700 }}>{v}</Tag> },
                  { title: 'Zones', dataIndex: 'unique_locations', align: 'center', width: 70 },
                  { title: '', width: 30, render: () => <RightOutlined style={{ color: '#bbb' }} /> },
                ]}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<Space><ClockCircleOutlined style={{ color: TEAL }} /> First 10 Arrivals</Space>} style={{ borderRadius: 10 }}>
            {firstArrivals.length === 0 ? <Empty /> : (
              <Table
                dataSource={firstArrivals}
                rowKey="id"
                pagination={false}
                size="small"
                onRow={(r) => ({ onClick: () => navigate(`/delegate-journey/${r.id}?event_id=${eventId}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: '#', width: 35, render: (_, __, i) => <Text strong>{i+1}</Text> },
                  { title: 'Name', dataIndex: 'delegate_name', render: (v, r) => (
                    <Space direction="vertical" size={0}>
                      <Text strong>{v}</Text>
                      {r.category && <Tag color={r.category_color} style={{ fontSize: 10 }}>{r.category}</Tag>}
                    </Space>
                  )},
                  { title: 'First Seen', dataIndex: 'first_seen', width: 130,
                    render: v => <Tag color="geekblue" style={{ fontFamily: 'monospace' }}>{fmtTime(v)}</Tag> },
                  { title: 'Location', dataIndex: 'first_location', width: 130,
                    render: v => v ? <Text style={{ fontSize: 12 }}>📍 {v}</Text> : '-' },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* ── Row: Capacity + Zone Flow ────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card title={<Space><AlertOutlined style={{ color: '#ef4444' }} /> Zone Capacity</Space>} style={{ borderRadius: 10 }}>
            {capacity.length === 0 ? <Empty /> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {capacity.map(z => {
                  const color = z.alert === 'critical' ? '#ef4444' : z.alert === 'warning' ? '#f59e0b' : TEAL;
                  return (
                    <div key={z.location_id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Space>
                          <EnvironmentOutlined style={{ color }} />
                          <Text strong>{z.location_name}</Text>
                          <Tag>{z.location_type || 'Zone'}</Tag>
                        </Space>
                        <Text style={{ color, fontWeight: 700, fontSize: 13 }}>
                          {z.current_count} / {z.capacity || '∞'}
                          {z.capacity > 0 && <Text type="secondary" style={{ marginLeft: 8 }}>({z.percent_full}%)</Text>}
                        </Text>
                      </div>
                      <Progress
                        percent={z.capacity > 0 ? Math.min(z.percent_full, 100) : 0}
                        strokeColor={color}
                        showInfo={false}
                        size="small"
                        status={z.alert === 'critical' ? 'exception' : 'normal'}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title={<Space><RiseOutlined style={{ color: PRIMARY }} /> Top Movement Flows</Space>}
            style={{ borderRadius: 10 }} extra={<Tooltip title="Most-walked paths between zones"><InfoIcon /></Tooltip>}>
            {zoneFlow.length === 0 ? <Empty description="No movements between zones yet" /> : (
              <Table
                dataSource={zoneFlow.slice(0, 12)}
                rowKey={(r, i) => i}
                pagination={false}
                size="small"
                columns={[
                  { title: 'From', dataIndex: 'from_name', render: v =>
                    <Tag color="default" style={{ fontWeight: 600 }}>{v || '—'}</Tag> },
                  { title: '→', width: 30, align: 'center', render: () => <RightOutlined style={{ color: TEAL }} /> },
                  { title: 'To', dataIndex: 'to_name', render: v =>
                    <Tag color="cyan" style={{ fontWeight: 600 }}>{v || '—'}</Tag> },
                  { title: 'Flow', dataIndex: 'flow_count', align: 'right', width: 80,
                    render: v => <Text strong style={{ color: PRIMARY }}>{v}</Text> },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* ── No-Shows Table ───────────────────────────────────────────────────── */}
      <Card
        title={<Space><AlertOutlined style={{ color: '#ef4444' }} /> No-Shows ({noShows.length})</Space>}
        style={{ borderRadius: 10, marginBottom: 16 }}
        extra={
          <Tag color={noShows.length === 0 ? 'green' : 'red'}>
            {summary.registered ? Math.round((noShows.length / summary.registered) * 100) : 0}% absent
          </Tag>
        }
      >
        {noShows.length === 0 ? (
          <Empty description={<Text type="success">All registered delegates have arrived 🎉</Text>} />
        ) : (
          <Table
            dataSource={noShows}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            columns={[
              { title: 'Name', dataIndex: 'delegate_name', render: v => <Text strong>{v}</Text> },
              { title: 'Category', dataIndex: 'category', render: (v, r) =>
                v ? <Tag color={r.category_color}>{v}</Tag> : '-', width: 110 },
              { title: 'Company', dataIndex: 'company', render: v => v || <Text type="secondary">-</Text> },
              { title: 'Email', dataIndex: 'email', render: v => v || <Text type="secondary">-</Text> },
              { title: 'Phone', dataIndex: 'phone', width: 130, render: v => v || <Text type="secondary">-</Text> },
              { title: 'RFID Tag', dataIndex: 'rfid_tag', width: 200,
                render: v => <Text code style={{ fontSize: 10 }}>{v}</Text> },
            ]}
          />
        )}
      </Card>
    </div>
  );
}

const InfoIcon = () => <span style={{ color: '#bbb', cursor: 'help' }}>ℹ</span>;
